/**
 * The audio engine, and the single source of truth for "where are we in the book".
 *
 * Three things make this less trivial than wrapping an <audio> element:
 *
 * 1. `timeupdate` fires about four times a second. At 1x that is a highlight that
 *    lags by up to 250 ms, which reads as sloppy; at 3x it is unusable. So the
 *    position is polled on animation frames while playing, and the element is only
 *    consulted for the raw currentTime.
 * 2. Finding the current sentence must be O(log n): a twenty hour book has tens of
 *    thousands of them, and this runs sixty times a second.
 * 3. Seeking to a sentence has to survive the audio not being buffered there yet.
 *    We set currentTime and let the browser sort it out, but we move the highlight
 *    immediately, because the user clicked and expects the text to answer at once.
 */

import { t } from "../i18n";
import { createAudioChain, type AudioChain } from "./audioChain";
import { rewindOnResume } from "./mediaSession";
import type { AudioPrefs } from "./audioPrefs";
import type { Sentence } from "./types";

export interface PlayerState {
  ready: boolean;
  playing: boolean;
  /** Index into the sentence array, or -1 before the first sentence. */
  index: number;
  time: number;
  duration: number;
  rate: number;
  error: string | null;
}

type Listener = (state: PlayerState) => void;

const RATES = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.5, 3];

// audioPrefs.ts imports `Player` (for the static `rates` array, to snap a saved
// rate onto a value this class actually offers). A plain top level import back
// here would create a load order cycle: whichever of the two modules loads
// first would read the other's export before it is defined, and audioPrefs.ts
// would silently fall back to its defaults the first time a real per book rate
// is on disk. A dynamic import breaks the cycle: this module finishes defining
// `Player` long before audioPrefs.ts is ever asked to run.
type AudioPrefsModule = typeof import("./audioPrefs");
let prefsApi: AudioPrefsModule | null = null;
const prefsApiReady: Promise<AudioPrefsModule> = import("./audioPrefs").then((module) => {
  prefsApi = module;
  return module;
});

// Mirrors the defaults in audioPrefs.ts. Only used for the brief window before
// the dynamic import above resolves.
const DEFAULT_PREFS: AudioPrefs = {
  skipBack: 15,
  skipForward: 30,
  rateByBook: {},
  boost: false,
  normalize: false,
  trimSilence: false,
};

/** Last sentence whose start is at or before `time`. -1 when there is none. */
export function sentenceAt(sentences: Sentence[], time: number): number {
  let low = 0;
  let high = sentences.length - 1;
  let found = -1;
  while (low <= high) {
    const middle = (low + high) >> 1;
    if (sentences[middle].start <= time) {
      found = middle;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }
  return found;
}

export class Player {
  private audio: HTMLAudioElement | null = null;
  private sentences: Sentence[] = [];
  private listeners = new Set<Listener>();
  private frame = 0;
  private bookId: string | null = null;
  private prefs: AudioPrefs = DEFAULT_PREFS;
  private unsubscribePrefs: (() => void) | null = null;
  private chain: AudioChain | null = null;
  /** Timestamp of the last pause, so the next play can rewind proportionally. */
  private pausedAt: number | null = null;
  private state: PlayerState = {
    ready: false,
    playing: false,
    index: -1,
    time: 0,
    duration: 0,
    rate: 1,
    error: null,
  };

  constructor() {
    void prefsApiReady.then((module) => {
      this.unsubscribePrefs = module.subscribe((prefs) => {
        this.prefs = prefs;
        this.chain?.update({ boost: prefs.boost, normalize: prefs.normalize, trimSilence: prefs.trimSilence });
      });
    });
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  get snapshot(): PlayerState {
    return this.state;
  }

  get current(): Sentence | null {
    return this.sentences[this.state.index] ?? null;
  }

  /** Seconds the rewind control jumps back, from the reader's saved preference. */
  get skipBackSeconds(): number {
    return this.prefs.skipBack;
  }

  /** Seconds the fast forward control jumps ahead, from the reader's saved preference. */
  get skipForwardSeconds(): number {
    return this.prefs.skipForward;
  }

  /**
   * Associates the player with a book so playback rate can be looked up and
   * saved per book. Call before `load()`; safe to call again for a new book.
   */
  setBook(bookId: string): void {
    this.bookId = bookId;
    const applySaved = (module: AudioPrefsModule) => {
      // The book may have changed again before this resolves; do not clobber it.
      if (this.bookId === bookId) this.applyRate(module.rateFor(bookId));
    };
    if (prefsApi) applySaved(prefsApi);
    else void prefsApiReady.then(applySaved);
  }

  /** Point the player at a book. Safe to call again with the same source. */
  load(url: string, sentences: Sentence[], startAt = 0, rate?: number): void {
    this.sentences = sentences;
    if (!this.audio) {
      this.audio = new Audio();
      this.audio.preload = "metadata";
      this.audio.addEventListener("loadedmetadata", this.onMetadata);
      this.audio.addEventListener("play", this.onPlay);
      this.audio.addEventListener("pause", this.onPause);
      this.audio.addEventListener("ended", this.onPause);
      this.audio.addEventListener("error", this.onError);
      this.audio.addEventListener("timeupdate", this.onTick);
    }
    if (this.audio.src !== new URL(url, location.href).href) {
      this.audio.src = url;
      this.patch({ ready: false, playing: false, error: null, time: startAt, index: -1 });
    }
    // No explicit rate: keep whatever setBook() already established for this
    // book. With no book set at all, this reproduces the exact behaviour from
    // before per book rates existed.
    const effectiveRate = rate ?? (this.bookId ? this.state.rate : 1);
    this.applyRate(effectiveRate);
    if (startAt > 0) this.seek(startAt);
  }

  play(): void {
    // The audio chain needs a user gesture to unlock its AudioContext, so it is
    // only ever created here, from a real play, never ahead of time.
    this.ensureChain();
    // A failed play() is almost always the browser's autoplay policy. Report it
    // rather than leaving a play button that silently does nothing.
    this.audio?.play().catch((error: unknown) => {
      this.patch({ error: error instanceof Error ? error.message : t("playbackRefused") });
    });
  }

  pause(): void {
    this.audio?.pause();
  }

  toggle(): void {
    this.state.playing ? this.pause() : this.play();
  }

  seek(time: number): void {
    if (!this.audio) return;
    const clamped = Math.max(0, this.state.duration ? Math.min(time, this.state.duration) : time);
    this.audio.currentTime = clamped;
    // Move the highlight now, without waiting for the browser to actually seek.
    this.patch({ time: clamped, index: sentenceAt(this.sentences, clamped) });
  }

  seekToSentence(index: number): void {
    const sentence = this.sentences[index];
    if (!sentence) return;
    this.seek(sentence.start);
    this.patch({ index });
  }

  nudge(seconds: number): void {
    this.seek(this.state.time + seconds);
  }

  setRate(rate: number): void {
    if (!this.audio) return;
    this.applyRate(rate);
    if (this.bookId) {
      const bookId = this.bookId;
      if (prefsApi) prefsApi.setRateFor(bookId, rate);
      else void prefsApiReady.then((module) => module.setRateFor(bookId, rate));
    }
  }

  /** Delegates the sleep timer's volume fade to the audio chain, or the raw
   * element when nothing has played yet this session and no chain exists. */
  setFade(factor: number): void {
    if (this.chain) {
      this.chain.setFade(factor);
    } else if (this.audio) {
      this.audio.volume = Math.min(1, Math.max(0, factor));
    }
  }

  cycleRate(direction: 1 | -1 = 1): void {
    const position = RATES.indexOf(this.state.rate);
    const next = RATES[Math.min(RATES.length - 1, Math.max(0, position + direction))];
    this.setRate(next ?? 1);
  }

  static get rates(): readonly number[] {
    return RATES;
  }

  destroy(): void {
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.chain?.destroy();
    this.chain = null;
    this.unsubscribePrefs?.();
    this.unsubscribePrefs = null;
    if (!this.audio) return;
    this.audio.pause();
    this.audio.removeEventListener("loadedmetadata", this.onMetadata);
    this.audio.removeEventListener("play", this.onPlay);
    this.audio.removeEventListener("pause", this.onPause);
    this.audio.removeEventListener("ended", this.onPause);
    this.audio.removeEventListener("error", this.onError);
    this.audio.removeEventListener("timeupdate", this.onTick);
    this.audio.src = "";
    this.audio = null;
    this.listeners.clear();
  }

  private applyRate(rate: number): void {
    if (this.audio) this.audio.playbackRate = rate;
    this.patch({ rate });
  }

  private ensureChain(): void {
    if (this.chain || !this.audio) return;
    this.chain = createAudioChain(this.audio, (seconds) => this.nudge(seconds));
    this.chain.update({
      boost: this.prefs.boost,
      normalize: this.prefs.normalize,
      trimSilence: this.prefs.trimSilence,
    });
  }

  private onMetadata = (): void => {
    this.patch({ ready: true, duration: this.audio?.duration ?? 0, error: null });
  };

  private onPlay = (): void => {
    if (this.pausedAt !== null) {
      const pausedMs = performance.now() - this.pausedAt;
      this.pausedAt = null;
      const rewind = rewindOnResume(pausedMs);
      if (rewind > 0) this.seek(this.state.time - rewind);
    }
    this.patch({ playing: true, error: null });
    this.tick();
  };

  private onPause = (): void => {
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.pausedAt = performance.now();
    this.patch({ playing: false });
  };

  private onError = (): void => {
    this.patch({ playing: false, error: t("playbackLoadFailed") });
  };

  /** Coarse updates while paused, so scrubbing still moves the highlight. */
  private onTick = (): void => {
    if (!this.state.playing) this.sample();
  };

  private tick = (): void => {
    this.sample();
    this.chain?.tick();
    if (this.state.playing) this.frame = requestAnimationFrame(this.tick);
  };

  private sample(): void {
    const time = this.audio?.currentTime ?? 0;
    const index = sentenceAt(this.sentences, time);
    if (index !== this.state.index || Math.abs(time - this.state.time) > 0.05) {
      this.patch({ time, index });
    }
  }

  private patch(change: Partial<PlayerState>): void {
    this.state = { ...this.state, ...change };
    for (const listener of this.listeners) listener(this.state);
  }
}
